import { useEffect, useRef, useState, useCallback } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { MapPin, UserCheck, Loader2, Navigation, Route } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";

const COLORS = { pendiente: "#C9A24B", aprobado: "#6D28D9", rechazado: "#64748b" };
const ESTADO_LBL = { pendiente: "Recibido", aprobado: "En ejecución", rechazado: "Cerrado" };
const dot = (color) => L.divIcon({
  className: "", iconSize: [18, 18], iconAnchor: [9, 9],
  html: `<span style="display:block;width:16px;height:16px;border-radius:9999px;background:${color};border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.35)"></span>`,
});

export default function FieldDispatch() {
  const mapRef = useRef(null);
  const mapObj = useRef(null);
  const markers = useRef([]);
  const routeLine = useRef(null);
  const [data, setData] = useState({ tickets: [], resumen: {} });
  const [techs, setTechs] = useState([]);
  const [sel, setSel] = useState(null);
  const [tech, setTech] = useState("");
  const [busy, setBusy] = useState(false);
  const [route, setRoute] = useState(null);
  const [routing, setRouting] = useState(false);

  const load = useCallback(() => {
    api.get("/field/tickets").then((r) => setData(r.data)).catch(() => {});
    api.get("/field/technicians").then((r) => setTechs(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  // init map once
  useEffect(() => {
    if (mapObj.current || !mapRef.current) return;
    const m = L.map(mapRef.current, { scrollWheelZoom: true }).setView([19.4326, -99.1332], 5);
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", { attribution: "© OpenStreetMap", maxZoom: 19 }).addTo(m);
    mapObj.current = m;
    setTimeout(() => m.invalidateSize(), 200);
    return () => { m.remove(); mapObj.current = null; };
  }, []);

  // render markers
  useEffect(() => {
    const m = mapObj.current;
    if (!m) return;
    markers.current.forEach((mk) => mk.remove());
    markers.current = [];
    const geo = data.tickets.filter((t) => t.geolocalizado);
    geo.forEach((t) => {
      const mk = L.marker([t.lat, t.lng], { icon: dot(COLORS[t.estado] || "#6D28D9") }).addTo(m);
      mk.bindTooltip(`${t.folio || "Ticket"} · ${t.empresa || ""}`);
      mk.on("click", () => { setSel(t); setTech(t.tecnico || ""); });
      markers.current.push(mk);
    });
    if (geo.length) {
      const b = L.latLngBounds(geo.map((t) => [t.lat, t.lng]));
      m.fitBounds(b.pad(0.3), { maxZoom: 13 });
    }
  }, [data]);

  const optimizeRoute = async () => {
    const m = mapObj.current; if (!m) return;
    setRouting(true);
    try {
      const c = m.getCenter();
      const { data: res } = await api.post("/field/route", { lat: c.lat, lng: c.lng, tecnico: tech || undefined });
      setRoute(res);
      if (routeLine.current) { routeLine.current.remove(); routeLine.current = null; }
      const path = [[c.lat, c.lng], ...res.orden.map((o) => [o.lat, o.lng])];
      if (path.length > 1) {
        routeLine.current = L.polyline(path, { color: "#6D28D9", weight: 3, opacity: 0.7, dashArray: "6 6" }).addTo(m);
        m.fitBounds(L.latLngBounds(path).pad(0.3), { maxZoom: 13 });
      } else { toast.error("No hay tickets geolocalizados para la ruta"); }
    } catch { toast.error("No se pudo optimizar la ruta"); } finally { setRouting(false); }
  };


  const assign = async () => {
    if (!sel || !tech) return;
    setBusy(true);
    try {
      await api.post(`/field/tickets/${sel.id}/assign`, { tecnico: tech });
      toast.success(`Ticket ${sel.folio || ""} asignado a ${tech}`);
      load();
      setSel((s) => s ? { ...s, tecnico: tech } : s);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Error"); } finally { setBusy(false); }
  };

  const r = data.resumen || {};
  const noGeo = data.tickets.filter((t) => !t.geolocalizado);

  return (
    <div data-testid="field-dispatch">
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div className="grid grid-cols-3 gap-3 max-w-md flex-1">
          {[["Tickets", r.total || 0], ["Geolocalizados", r.geolocalizados || 0], ["Sin asignar", r.sin_asignar || 0]].map(([l, v]) => (
            <div key={l} className="card-surface p-3"><div className="text-[11px] text-navy/40">{l}</div><div className="text-lg font-medium">{v}</div></div>
          ))}
        </div>
        <button onClick={optimizeRoute} disabled={routing} className="btn btn-outline text-sm" data-testid="dispatch-optimize">
          {routing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Route className="w-4 h-4" />} Optimizar ruta{route ? ` · ${route.distancia_total_km} km` : ""}
        </button>
      </div>
      <div className="grid lg:grid-cols-[1.7fr_1fr] gap-4">
        <div ref={mapRef} data-testid="dispatch-map" className="rounded-xl overflow-hidden border border-navy/10" style={{ height: "62vh", zIndex: 0 }} />
        <div className="space-y-3">
          {sel ? (
            <div className="card-surface p-5" data-testid="dispatch-panel">
              <div className="flex items-center gap-2 mb-1"><MapPin className="w-4 h-4 text-violet" /><span className="font-medium">{sel.folio || "Ticket"}</span>
                <span className="ml-auto text-[11px] px-2 py-0.5 rounded-full" style={{ background: `${COLORS[sel.estado]}22`, color: COLORS[sel.estado] }}>{ESTADO_LBL[sel.estado] || sel.estado}</span></div>
              <div className="text-sm text-navy/60">{sel.empresa} · {sel.tipo}</div>
              <div className="text-xs text-navy/45 mt-1">{sel.direccion}{sel.ciudad ? `, ${sel.ciudad}` : ""}</div>
              {sel.tecnico && <div className="text-xs text-emerald-600 mt-2 flex items-center gap-1"><UserCheck className="w-3.5 h-3.5" /> {sel.tecnico}</div>}
              <div className="mt-4">
                <div className="overline text-navy/40 mb-1.5">Asignar técnico</div>
                <select value={tech} onChange={(e) => setTech(e.target.value)} className="field-sm w-full mb-2" data-testid="dispatch-tech-select">
                  <option value="">Elige un técnico…</option>
                  {techs.map((tt) => <option key={tt.id} value={tt.nombre}>{tt.nombre}{tt.zona ? ` · ${tt.zona}` : ""}</option>)}
                </select>
                <button onClick={assign} disabled={busy || !tech} className="btn btn-primary text-sm w-full" data-testid="dispatch-assign">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserCheck className="w-4 h-4" />} Asignar
                </button>
              </div>
            </div>
          ) : (
            <div className="card-surface p-5 text-sm text-navy/40 flex items-center gap-2"><Navigation className="w-4 h-4" /> Selecciona un ticket en el mapa para asignarlo.</div>
          )}

          {noGeo.length > 0 && (
            <div className="card-surface p-4">
              <div className="overline text-navy/40 mb-2">Sin geolocalizar ({noGeo.length})</div>
              <div className="space-y-1.5 max-h-48 overflow-y-auto thin-scroll">
                {noGeo.map((t) => (
                  <div key={t.id} className="text-xs flex justify-between border-b border-navy/5 py-1" data-testid={`dispatch-nogeo-${t.folio || t.id}`}>
                    <span className="truncate">{t.folio || "Ticket"} · {t.empresa}</span>
                    <span className="text-navy/40">{t.tecnico || "sin asignar"}</span>
                  </div>
                ))}
                <p className="text-[11px] text-navy/40 pt-1">Captura Latitud/Longitud en el ticket para ubicarlo en el mapa.</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
